import { ApiError } from './ApiError';
import { getErrorMessage } from './getErrorMessage';

export type ApiFormikError = Record<string, any>;

export type ApiErrorKeyMap = string;

interface FormikErrorFormatOptions<TFormValues extends Record<string, any>> {
  defaultMessage?: string;

  /**
   * Provide a key-value pair to override keys.
   * The object's key is the original value, the value is the desired key.
   */
  errorFieldMap?: Record<string, keyof TFormValues>;
}

/**
 * If you build support for a function "errorFieldMap", include this documentation:
 *
 * You can also provide a function as a value to perform some transformation
 * on the key or value. In this case, it returns a key-value pair in place of
 * the original key.
 */
export function formatFormikError<TFormValues extends Record<string, any> = {}>(
  error: ApiError | unknown,
  options?: FormikErrorFormatOptions<TFormValues>,
): ApiFormikError {
  const realError = error as ApiError;
  const errors: ApiFormikError = {};
  realError?.errors?.forEach(err => {
    const message = err.message || err.detail;

    if (err.field) {
      // There's a possibility (apparently) that the API can come back with
      // an object notation error object.
      if (typeof message === 'string') {
        const keyMapElement = options?.errorFieldMap?.[err.field];
        if (keyMapElement) {
          if (keyMapElement === '_message') {
            // @ts-ignore
            errors[keyMapElement] = `${err.field}: ${message}`;
          } else {
            // @ts-ignore
            errors[keyMapElement] = message;
          }
        } else {
          errors[err.field] = message;
        }
      }
    } else {
      errors._message = message;
    }
  });

  if (Object.keys(errors).length === 0) {
    errors._message =
      options?.defaultMessage ||
      getErrorMessage(realError) ||
      'Something went wrong...';
  }

  return errors;
}
